import React, { useMemo } from 'react';

import { FormProps } from '@components/admin/FormDialog';
import { TextInput } from '@components/admin/Forms';
import { LogForm } from '@components/Form';
import { CheckboxInput } from '@components/Forms/CheckboxInput';
import { FormAlert } from '@components/Forms/FormAlert';
import { ManagementFormItem } from '@components/Forms/ManagementFormItem';
import { ManagementTextInput } from '@components/Forms/ManagementTextInput';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { instructionStrings } from '@data/instruction';
import { usePatchBookingInquiry } from '@hooks/mutation/dashboard/usePatchBookingInquiry';
import { usePatchCustomsInstruction } from '@hooks/mutation/dashboard/usePatchCustomsInstruction';
import { usePatchDeliveryInstruction } from '@hooks/mutation/dashboard/usePatchDeliveryInstruction';
import { AdminInquiryDetail } from '@models/BookingInquiry';
import {
  type BookingInquirySpecialNoteForm as BookingInquirySpecialNoteFormT,
  validateBookingInquirySpecialNoteForm,
} from '@models/BookingInquiryUtils';
import { CustomsInstructionSpecialNoteItem } from '@models/CustomsInstruction';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { Form, Formik, useField } from 'formik';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
  },
  customsInstructionSpecialNoteContainer: {
    ...common.flexColumn,
    flex: 1,
  },
  specialNoteTitle: {
    ...common.textGray900,
    marginBottom: '0.25rem',
  },
}));

const BookingInquirySpecialNoteFormInput = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [hasSpecialNoteField] = useField<boolean>('has_special_note');

  const [customsInstructionField] = useField<unknown>('customs_instruction');
  const [customsInstructionHasSpecialNoteField] = useField<boolean>(
    'customs_instruction.has_special_note',
  );
  const [customsInstructionSpecialNoteField] = useField<CustomsInstructionSpecialNoteItem[]>(
    'customs_instruction.special_note',
  );
  const [customsInstructionSpecialNoteTitlesField] = useField<string[]>(
    'customs_instruction.special_note_titles',
  );

  const [deliveryInstructionField] = useField<unknown>('delivery_instruction');

  const [deliveryInstructionHasSpecialNoteField] = useField<boolean>(
    'delivery_instruction.has_special_note',
  );

  return (
    <>
      {customsInstructionField.value && (
        <>
          <ManagementTextInput
            name="customs_instruction.has_special_note"
            label={instructionStrings.customsInstructionSpecialNote}
            choose
            options={[
              {
                value: true,
                label: instructionStrings.customsInstructionHasSpecialNoteTrue,
              },
              {
                value: false,
                label: instructionStrings.customsInstructionHasSpecialNoteFalse,
              },
            ]}
          />

          {customsInstructionHasSpecialNoteField.value && (
            <>
              <ManagementFormItem name="customs_instruction.special_note_titles">
                <div className={classes.customsInstructionSpecialNoteContainer}>
                  {customsInstructionSpecialNoteField.value.map((item, i) => {
                    return (
                      <div key={item.title}>
                        <CheckboxInput
                          name="customs_instruction.special_note_titles"
                          value={item.title}
                          label={item.title}
                          classes={{ container: classes.specialNoteTitle }}
                        />

                        {customsInstructionSpecialNoteTitlesField.value.includes(item.title) && (
                          <div style={{ paddingLeft: 22, paddingTop: 8, paddingBottom: 8 }}>
                            <TextInput
                              name={`customs_instruction.special_note.${i}.note`}
                              noLabel
                              placeholder={instructionStrings.customsInstructionSpecialNotePlaceholder(
                                item.title,
                              )}
                              multiline
                              rows={2}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ManagementFormItem>
            </>
          )}
        </>
      )}

      {deliveryInstructionField.value && (
        <>
          <ManagementTextInput
            name="delivery_instruction.has_special_note"
            label={instructionStrings.deliveryInstructionSpecialNoteShort}
            choose
            options={[
              {
                value: true,
                label: instructionStrings.deliveryInstructionHasSpecialNoteTrue,
              },
              {
                value: false,
                label: instructionStrings.deliveryInstructionHasSpecialNoteFalse,
              },
            ]}
          />
          {deliveryInstructionHasSpecialNoteField.value && (
            <ManagementTextInput name="delivery_instruction.special_note" label="" multiline />
          )}
        </>
      )}

      <ManagementTextInput
        name="has_special_note"
        label={bookingInquiryStrings.selfSpecialNote}
        choose
        options={[
          {
            value: true,
            label: bookingInquiryStrings.hasSpecialNoteTrue,
          },
          {
            value: false,
            label: bookingInquiryStrings.hasSpecialNoteFalse,
          },
        ]}
      />

      {hasSpecialNoteField.value && <ManagementTextInput name="special_note" label="" multiline />}
    </>
  );
};

export interface BookingInquirySpecialNoteProps extends FormProps {
  adminInquiryDetail: AdminInquiryDetail;
}

/**
 * This form handles the both conditions whether related customs_instruction/delivery_instruction are
 * present or not. For not present cases, the form hides related input, validations and API calls.
 */
export const BookingInquirySpecialNoteForm: React.FC<BookingInquirySpecialNoteProps> = (props) => {
  const { formikRef, adminInquiryDetail } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const patchBookingInquiry = usePatchBookingInquiry(adminInquiryDetail.d2d_no);
  const patchCustomsInstruction = usePatchCustomsInstruction(adminInquiryDetail.d2d_no);
  const patchDeliveryInstruction = usePatchDeliveryInstruction(adminInquiryDetail.d2d_no);

  const initialValues = useMemo(
    () => ({
      has_special_note: adminInquiryDetail.has_special_note,
      special_note: adminInquiryDetail.special_note,
      customs_instruction: adminInquiryDetail.customs_instruction && {
        ...adminInquiryDetail.customs_instruction,
        special_note_titles: adminInquiryDetail.customs_instruction.special_note
          .filter((s) => s.selected)
          .map((s) => s.title),
      },
      delivery_instruction: adminInquiryDetail.delivery_instruction,
    }),
    [adminInquiryDetail],
  );

  return (
    <Formik<BookingInquirySpecialNoteFormT>
      innerRef={formikRef}
      initialValues={initialValues}
      validate={validateBookingInquirySpecialNoteForm}
      onSubmit={async (values) => {
        if (values.customs_instruction) {
          await patchCustomsInstruction.mutateAsync({
            ...values.customs_instruction,
            special_note: values.customs_instruction.special_note.map((note) => ({
              ...note,
              selected: values.customs_instruction!.special_note_titles.includes(note.title),
            })),
          });
        }
        if (values.delivery_instruction) {
          await patchDeliveryInstruction.mutateAsync(values.delivery_instruction);
        }

        await patchBookingInquiry.mutateAsync(values);
      }}
    >
      <Form>
        <LogForm name="BookingInquirySpecialNoteForm" />
        <SubmittingOverlay />
        <FormAlert mutation={patchBookingInquiry} />

        <div className={classes.container}>
          <BookingInquirySpecialNoteFormInput />
        </div>
      </Form>
    </Formik>
  );
};
